import { InjectionToken } from '@angular/core';
import { BehaviorSubject, take, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';

class ComponentSidePanelConfig {
}
const PANEL_DATA = new InjectionToken('PanelData');

class DataSource {
    get isLoading() {
        return this.loadingSubject.getValue();
    }
    get willDestroyWithTable() {
        return this.destroyWithTable;
    }
    set onLoad(load) {
        this.loadFunc = load;
    }
    set onExtract(extract) {
        this.extractFunc = extract;
    }
    constructor(apiService, snackbarService = null, destroyWithTable = true) {
        this.apiService = apiService;
        this.snackbarService = snackbarService;
        this.destroyWithTable = destroyWithTable;
        this.params = { request: null };
        this.lastIndex = 0;
        this.offset = 0;
        this.limit = 20;
        this.totalItems = 0;
        this.data = [];
        this.dataSubject = new BehaviorSubject([]);
        this.loadingSubject = new BehaviorSubject(false);
        this.loading$ = this.loadingSubject.asObservable();
        this.emptySubject = new BehaviorSubject(false);
        this.empty$ = this.emptySubject.asObservable();
        this.extractFunc = (response) => [];
    }
    connect() {
        this.data$ = this.dataSubject.asObservable();
        return this.data$;
    }
    disconnect() {
        this.dataSubject.complete();
        this.loadingSubject.complete();
    }
    load(params, body) {
        this.setParams(params);
        this.setBody(body);
        this.beforeLoadData();
        this.unsubscribeData();
        this.dataSubscription = this.loadData().pipe(take(1)).subscribe(data => {
            // @ts-ignore
            this.afterLoadData(data);
        });
    }
    beforeLoadData() {
        this.loadingSubject.next(true);
        this.emptySubject.next(false);
    }
    afterLoadData(data) {
        this.lastIndex = data.length - 1;
        this.data = data;
        this.dataSubject.next(data);
        this.emptySubject.next(data.length === 0);
        this.loadingSubject.next(false);
    }
    setParams(params) {
        this.params = params;
        this.limit = params?.limit ? params.limit : 20;
    }
    setBody(body) {
        this.body = body;
    }
    unsubscribeData() {
        if (this.dataSubscription) {
            this.dataSubscription.unsubscribe();
        }
    }
    loadData() {
        if (!this.loadFunc) {
            return of();
        }
        /*    const mediaQuery = window.matchMedia('(min-height: 1300px)');
            if (mediaQuery.matches) {
              this.params.request.limit = 50;
              this.params.request.offset = this.params.request.offset ? this.params.request.offset + 30 : '';
            }*/
        return this.loadFunc(this.apiService, this.params, this.body).pipe(map(result => {
            this.data = this.extractFunc(result);
            if (this.allSelected) {
                for (const dataItem of this.data) {
                    dataItem.selected = true;
                }
            }
            this.offset = result.offset;
            this.totalItems = result.total_items;
            return this.data;
        }), catchError(error => {
            this.emptySubject.next(true);
            this.loadingSubject.next(false);
            if (this.snackbarService) {
                this.snackbarService.error(error.userMessage);
            }
            return of();
        }));
    }
    addPage() {
        if (!this.isLoading && this.params
            && this.lastIndex > this.offset + this.limit - 5 && this.lastIndex < this.totalItems) {
            const request = this.params;
            request.offset = this.offset + this.limit;
            request.limit = this.limit;
            this.beforeLoadData();
            this.unsubscribeData();
            this.dataSubscription = this.loadData().pipe(take(1)).subscribe(data => {
                this.afterLoadData([
                    ...this.dataSubject.getValue(),
                    // @ts-ignore
                    ...data
                ]);
            });
        }
    }
    onScroll(page) {
        if (!this.isLoading && this.offset < this.totalItems) {
            ++page;
            const request = this.params;
            request.offset = this.offset + this.limit;
            request.limit = this.limit;
            this.beforeLoadData();
            this.unsubscribeData();
            this.dataSubscription = this.loadData().pipe(take(1)).subscribe(data => {
                this.afterLoadData([
                    ...this.dataSubject.getValue(),
                    ...data
                ]);
            });
        }
    }
    sort(orderBy, orderByDirection) {
        if (this.params) {
            const request = this.params.order(orderBy, orderByDirection);
            request.offset = 0;
            request.limit = this.limit;
            this.load(request);
        }
    }
    search(key) {
        if (this.params) {
            this.load(this.params.setSearch(key).setOffset(0));
        }
    }
    filter(filters, orderBy, orderByDirection, additionalFields = {}) {
        if (this.params) {
            const request = orderBy && orderByDirection ? this.params.setFilter(filters).setOffset(0).order(orderBy, orderByDirection.toUpperCase()).setAdditionalFields(additionalFields) :
                this.params.setFilter(filters).setOffset(0).setAdditionalFields(additionalFields);
            this.load(request);
        }
    }
}

var OrderByDirection;
(function (OrderByDirection) {
    OrderByDirection["DESC"] = "DESC";
    OrderByDirection["ASC"] = "ASC";
})(OrderByDirection || (OrderByDirection = {}));
class RequestParameters {
    constructor(filter, search = '', limit = 20, offset = 0, orderBy, orderByDirection, additionalFields = null, active_only) {
        this.limit = limit;
        this.offset = offset;
        this.orderBy = orderBy;
        this.orderByDirection = orderByDirection;
        this.search = search;
        this.filter = filter;
        this.additionalFields = additionalFields || {};
        this.active_only = active_only;
    }
    setAdditionalFields(fields) {
        return new RequestParameters(this.filter, this.search, this.limit, this.offset, this.orderBy, this.orderByDirection, fields);
    }
    setSearch(search) {
        if (!search) {
            search = '';
        }
        return new RequestParameters(this.filter, search, this.limit, this.offset, this.orderBy, this.orderByDirection, this.additionalFields);
    }
    setFilter(filter) {
        return new RequestParameters(filter, this.search, this.limit, this.offset, this.orderBy, this.orderByDirection, this.additionalFields);
    }
    setLimit(limit) {
        return new RequestParameters(this.filter, this.search, limit, this.offset, this.orderBy, this.orderByDirection, this.additionalFields);
    }
    setOffset(offset) {
        return new RequestParameters(this.filter, this.search, this.limit, offset, this.orderBy, this.orderByDirection, this.additionalFields);
    }
    toHttpParams() {
        let params = new HttpParams()
            .set('limit', `${this.limit}`)
            .set('offset', `${this.offset}`);
        if (this.orderBy) {
            params = params.set('order_by', this.orderBy);
        }
        if (this.orderByDirection) {
            params = params.set('order_by_direction', this.orderByDirection);
        }
        if (this.filter !== undefined && this.filter !== null && this.filter !== '') {
            params = params.set('filter', this.filter);
        }
        if (this.additionalFields !== null) {
            for (const key of Object.keys(this.additionalFields)) {
                if (Array.isArray(this.additionalFields[key])) {
                    for (const value of this.additionalFields[key]) {
                        params = params.append(key, value);
                    }
                }
                else {
                    params = params.set(key, this.additionalFields[key]);
                }
            }
        }
        params = params.set('search', this.search);
        if (this.active_only || this.active_only === false) {
            params = params.set('active_only', this.active_only);
        }
        return params;
    }
    order(orderBy, orderByDirection) {
        return new RequestParameters(this.filter, this.search, this.limit, this.offset, orderBy, orderByDirection, this.additionalFields);
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { ComponentSidePanelConfig, DataSource, OrderByDirection, PANEL_DATA, RequestParameters };
